import React, { useEffect, useState } from "react";
import i18next from "i18next";
import {
  EMPTY_RESULTS_SHOW,
  FIELD_REQUIRED,
  LOADING,
} from "../../../../i18n/keysTranslations";
import { Controller, useController } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslationApp } from "../../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetMerchantsGoogleAds } from "../../../../actions/adsCampaigns";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import FieldCaption from "../../../Form/FieldCaption";

const SelectMerchantGoogle = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  integrationID,
  adAccountID,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  onChangeAction = () => {},
  filteredValues = [],
  sxTextField = {},
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const {
    field: { value: valueField },
  } = useController({ control, name });

  const onGetMerchantsGoogleAds = async () => {
    setLoading(true);
    const response = await dispatch(
      startGetMerchantsGoogleAds({
        integrationID,
        adAccountID,
      })
    );
    if (response.ok) {
      const data = response.data;
      if (data?.length > 0) {
        setOptions(data);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    onGetMerchantsGoogleAds();
    // eslint-disable-next-line
  }, [integrationID, adAccountID]);

  useEffect(() => {
    if (!valueField) return;
    const productSet = options.find((opt) => opt.id === valueField);
    onChangeAction(productSet || valueField);

    // eslint-disable-next-line
  }, [options]);

  const getOptionLabel = (option) => {
    return `${option?.name}`;
  };

  return (
    <>
      <Controller
        defaultValue={""}
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => (
          <Autocomplete
            {...field}
            id={name}
            hasLabel={Boolean(label)}
            fullWidth={fullWidth}
            disabled={disabled}
            loadingText={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={20} />
                {t(LOADING)}
              </Box>
            }
            noOptionsText={t(EMPTY_RESULTS_SHOW)}
            loading={loading}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onChange={(_, value) => {
              if (value?.id) {
                field.onChange(value.id);
                onChangeAction(value);
              } else {
                field.onChange(null);
                onChangeAction(null);
              }
            }}
            isOptionEqualToValue={(option, value) => option?.id === value}
            getOptionLabel={(option) => {
              if (option == null || option === "") return "";
              const optionValue = options.find((opt) => opt.id === option);
              if (optionValue) {
                return getOptionLabel(optionValue);
              }
              if (typeof option === "string") {
                return option;
              }
              return option?.name || "";
            }}
            options={options.filter((opt) => !filteredValues.includes(opt.id))}
            renderOption={(props, option) => {
              return (
                <Box
                  {...props}
                  component="li"
                  key={option.id}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="body2">
                    {getOptionLabel(option)}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                hiddenLabel={!label}
                variant={variant}
                placeholder={field?.value?.length > 0 ? "" : placeholder}
                error={!!error}
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  ...sxTextField,
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        )}
      />
      <FieldCaption error={error} helperText={helperText} />
    </>
  );
};

export default SelectMerchantGoogle;
